import Bugsnag from '@bugsnag/js';
import { QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createRootRouteWithContext, HeadContent, Outlet } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { isEqual } from 'lodash';
import React from 'react';

import { getEnv } from '@/app/conf/env';
import { AppToastContainer } from '@/app/lib/react-toastify';
import { queryPersister } from '@/app/lib/tanstack-query';
import { CurrentUserResponse } from '@/app/network/models/v1/user';
import { Heading } from '@/common/components/heading';
import { Spinner } from '@/common/components/spinner';
import { EmptyState, EmptyStateImage, EmptyStateMessage } from '@/shared/components/empty-states';
import { MainLayout } from '@/shared/layouts/main-layout';
import { userKeys } from '@/shared/user/queries';
import { fullName } from '@/shared/users';

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
  ensureUser: () => Promise<CurrentUserResponse>;
}>()({
  component: RootComponent,
  notFoundComponent: NotFoundComponent
});

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  // maintain user state with Bugsnag
  React.useEffect(() => {
    const syncUserToBugsnag = () => {
      const user = queryClient.getQueryData<CurrentUserResponse>(userKeys.authenticated());

      if (!!Bugsnag && Bugsnag.isStarted()) {
        Bugsnag.setUser(
          user?.id?.toString() || null,
          user?.email || null,
          fullName(user?.first_name, user?.last_name) || null
        );
      }
    };

    syncUserToBugsnag(); // ensure user is synced on mount

    // sync the user whenever the cache changes
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      const shouldListen = ['added', 'removed', 'updated'];

      if (shouldListen.includes(event.type)) {
        if (isEqual(event.query.queryKey, userKeys.authenticated())) {
          syncUserToBugsnag();
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [queryClient]);

  return (
    <PersistQueryClientProvider client={queryClient} persistOptions={{ persister: queryPersister, buster: getEnv().APP_VERSION }}>
      {getEnv().DEBUG_TANSTACK_QUERY && (<ReactQueryDevtools />)}
      <HeadContent />
      <AppToastContainer />
      <React.Suspense
        fallback={(
          <div className="flex h-screen items-center justify-center">
            <Spinner color="primary" size="xl" />
            <p className="text-center text-zinc-950/50">Loading...</p>
          </div>
        )}
      >
        <MainLayout>
          <Outlet />
          {getEnv().DEBUG_TANSTACK_ROUTER && (<TanStackRouterDevtools />)}
        </MainLayout>
      </React.Suspense>
    </PersistQueryClientProvider>
  );
}

export function NotFoundComponent() {
  return (
    <div className="flex flex-col items-center justify-center h-full">
      <EmptyState>
        <Heading>Not Found</Heading>
        <EmptyStateImage src="/img/bad_reception.svg" alt="satilite dish broadcasting signal" />
        <EmptyStateMessage>
          Sorry, we couldn't find the page or resource you were looking for.
        </EmptyStateMessage>
      </EmptyState>
    </div>
  );
}
